import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Headset, Mail, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/drawer';
import { api } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useSupport, type SupportMode } from '@/lib/support-context';
import { usePublicConfig, useLegalIdentityDisplay } from '@/lib/public-config';
import { TurnstileWidget, HoneypotField } from '@/components/turnstile-widget';
import { BRAND, getSupportContactLabel } from '@maintenance-tracker/shared';
import { cn } from '@/lib/utils';

const HINT_COPY: Record<'import_help', { title: string; body: string }> = {
  import_help: {
    title: 'Need help importing?',
    body: "Send us your spreadsheet — we'll import it for you. Tap Help below.",
  },
};

function resolveMode(context?: string, explicit?: SupportMode): SupportMode {
  if (explicit) return explicit;
  if (
    context === 'feature_idea' ||
    context?.startsWith('feedback_') ||
    context === 'help_page_idea'
  ) {
    return 'idea';
  }
  return 'help';
}

function resolveContext(context: string | undefined, mode: SupportMode, hint: string | null): string {
  if (context) return context;
  if (hint === 'import_help') return 'import_help';
  return mode === 'idea' ? 'feature_idea' : 'general';
}

export function SupportWidget() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { hint, registerOpenHandler } = useSupport();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<string | undefined>();
  const [mode, setMode] = useState<SupportMode>('help');
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const [sent, setSent] = useState(false);
  const [dismissedHint, setDismissedHint] = useState(false);

  const loggedIn = Boolean(user?.user);
  const { data: publicConfig } = usePublicConfig();
  const display = useLegalIdentityDisplay();
  const supportLabel = getSupportContactLabel(display);
  const turnstileSiteKey = loggedIn ? null : (publicConfig?.turnstileSiteKey ?? null);

  useEffect(() => {
    registerOpenHandler((ctx, options) => {
      const nextMode = resolveMode(ctx, options?.mode);
      setContext(ctx);
      setMode(nextMode);
      setMessage(options?.message ?? '');
      setOpen(true);
      setSent(false);
    });
  }, [registerOpenHandler]);

  useEffect(() => {
    if (!open) {
      setMessage('');
      if (!loggedIn) {
        setName('');
        setEmail('');
        setWebsite('');
        setTurnstileToken('');
      }
    }
  }, [open, loggedIn]);

  const sendMutation = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; id: string }>('/api/v1/support', {
        method: 'POST',
        body: JSON.stringify({
          message: message.trim(),
          ...(loggedIn
            ? {}
            : {
                name: name.trim() || undefined,
                email: email.trim(),
                website: website || undefined,
                turnstile_token: turnstileToken || undefined,
              }),
          page_path: pathname,
          context: resolveContext(context, mode, hint),
        }),
      }),
    onSuccess: () => {
      setSent(true);
      setMessage('');
      if (mode === 'idea' || context?.startsWith('feedback_')) {
        void qc.invalidateQueries({ queryKey: ['feedback-prompt'] });
      }
    },
  });

  const showHint = hint === 'import_help' && !dismissedHint && !open;
  const hintCopy = hint ? HINT_COPY[hint] : null;
  const isIdea = mode === 'idea';

  return (
    <>
      {showHint && hintCopy && (
        <div className="fixed bottom-24 right-4 z-40 max-w-[260px] animate-fade-in max-sm:right-3 max-sm:max-w-[calc(100vw-1.5rem)]">
          <div className="relative rounded-xl border border-brand/25 bg-bg-surface p-3.5 shadow-card">
            <button
              type="button"
              aria-label="Dismiss tip"
              onClick={() => setDismissedHint(true)}
              className="absolute right-2 top-2 rounded-md p-1 text-text-muted hover:bg-bg-subtle hover:text-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <p className="pr-6 text-sm font-medium text-text-primary">{hintCopy.title}</p>
            <p className="mt-1 text-[13px] leading-snug text-text-secondary">{hintCopy.body}</p>
            <div className="absolute -bottom-2 right-8 h-4 w-4 rotate-45 border-b border-r border-brand/25 bg-bg-surface" />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setContext(hint === 'import_help' ? 'import_help' : undefined);
          setMode('help');
          setOpen(true);
          setSent(false);
        }}
        className="fixed bottom-5 right-5 z-40 flex h-12 items-center justify-center gap-2 rounded-full bg-brand px-3.5 text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 max-sm:bottom-4 max-sm:right-4 max-sm:px-3"
        aria-label="Get help"
      >
        <Headset className="h-5 w-5 shrink-0" />
        <span className="hidden text-sm font-semibold min-[420px]:inline">Help</span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={isIdea ? 'Tell us what would help your shop' : 'Get help'}
        description={
          isIdea
            ? "We build and adjust features based on what shop owners ask for. We'll reply to your email."
            : loggedIn
              ? `We'll reply to ${user?.user.email}.`
              : "Tell us how we can help — we'll get back by email."
        }
      >
        {sent ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{isIdea ? 'Thanks — we read every idea' : 'Message sent'}</p>
                <p className="mt-1 text-[13px]">
                  {isIdea
                    ? 'We typically reply within one business day with next steps.'
                    : 'We typically reply within one business day.'}
                </p>
              </div>
            </div>
            <Button onClick={() => setOpen(false)} className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              sendMutation.mutate();
            }}
          >
            {loggedIn && (
              <div className="flex rounded-lg border border-border bg-bg-subtle/40 p-1">
                <button
                  type="button"
                  onClick={() => setMode('help')}
                  className={cn(
                    'flex-1 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                    !isIdea ? 'bg-bg-surface text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary',
                  )}
                >
                  Get help
                </button>
                <button
                  type="button"
                  onClick={() => setMode('idea')}
                  className={cn(
                    'flex-1 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                    isIdea ? 'bg-bg-surface text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary',
                  )}
                >
                  Suggest something
                </button>
              </div>
            )}

            {!loggedIn && (
              <>
                <HoneypotField
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
                <Input
                  label="Your email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  label="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </>
            )}

            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">
                {isIdea ? 'What would help your shop?' : 'Message'}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                required
                placeholder={
                  isIdea
                    ? 'Describe a feature, report, or workflow that would save you time…'
                    : context === 'import_help' || hint === 'import_help'
                      ? 'Paste a link to your spreadsheet or describe what you need imported…'
                      : 'How can we help?'
                }
                className="w-full rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm transition-colors placeholder:text-text-muted focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
              />
            </div>

            {!loggedIn && turnstileSiteKey && (
              <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />
            )}

            {sendMutation.isError && (
              <p className="text-[13px] text-danger">{(sendMutation.error as Error).message}</p>
            )}

            <Button
              type="submit"
              loading={sendMutation.isPending}
              disabled={!message.trim() || Boolean(!loggedIn && turnstileSiteKey && !turnstileToken)}
              className="w-full"
            >
              {isIdea ? 'Send idea' : 'Send message'}
            </Button>

            <div className="rounded-lg border border-border bg-bg-subtle/50 px-3 py-2.5 text-[13px] text-text-secondary">
              <div className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
                <p>
                  Prefer email? Contact{' '}
                  <a
                    href={`mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(
                      isIdea ? 'Renewal Drive feature idea' : 'Renewal Drive support',
                    )}`}
                    className="font-medium text-brand hover:underline"
                  >
                    {supportLabel}
                  </a>{' '}
                  for direct assistance.
                </p>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
